import axios from "axios";
import { User } from "./models/user-model";
import {
  AccessKeyAllResponse,
  AccessKeyArray,
  AccessKeyCreateResponse,
  MessageResponse,
} from "./models/access-key-model";
import {
  AccessPoint,
  AccessPointArray,
  AccessPointRelation,
} from "./models/access-points";
import { StrapiUnitRelation, Unit, UnitArray } from "./models/units";
import { PhotoTypesArray } from "./models/photo-types-model";
import { PropertyArray } from "./models/property-model";
import { MediaImageSingle, MediaResponse } from "./models/media-model";
import {
  AccessGroups,
  AccessGroupsArray,
  AccessGroupsRelation,
} from "./models/access-groups";
import { UserRoles } from "./models/role-model";
import { ProviderArray } from "./models/provider";
import {
  OneTimeVisitor,
  OneTimeVisitorArray,
  OneTimeVisitorRelation,
} from "./models/one-time-visitor";
import {
  UserAccessGroups,
  UserAccessGroupsArray,
} from "./models/user-access-groups";
import { Transaction, TransactionRelation } from "./models/transaction-model";
import { Access, AccessArray, AccessRelation } from "./models/access-model";
import {
  StrapiTransactionPhotoRelation,
  TransactionPhoto,
} from "./models/transaction-photo-model";
import {
  RegenerateVerifiableIdResponse,
  SingleVerifiableId,
  StrapiVerifiableIdRelation,
  VerifiableIdArray2,
} from "./models/verifiable-id";
import {
  AuthorizationPass,
  AuthorizationPassRelation,
} from "./models/authorization-pass";

const apiToken = process.env.REACT_APP_API_TOKEN;

//PRODUCTION
export const instance = axios.create({
  baseURL: "https://strapi.passtrack.mx",
  timeout: 30000,
});

//STAGING
// export const instance = axios.create({
//   baseURL: "https://strapi-testing.passtrack.mx",
//   timeout: 30000,
// });

// LOCAL
// export const instance = axios.create({
//   baseURL: "http://localhost:1339/",
//   timeout: 30000,
// });

//LOGIN------------------------------------------------------->>

export interface LoginPayload {
  identifier: string;
  password: string;
}

export interface LoginResponse {
  jwt: string;
  user: User;
}

const loginRequest = async (params: LoginPayload) => {
  const { data }: { data: LoginResponse } = await instance.post(
    "/api/auth/local",
    params,
  );

  return data;
};

//------------------------------------------------------------>>
//USERS------------------------------------------------------->>

const userByIdRequest = async (userId: number, token: string) => {
  const { data }: { data: User } = await instance.get(
    `/api/users/${userId}?populate[0]=access_points&populate[1]=company&populate[2]=created_by_users&populate[3]=property.image&populate[4]=role&populate[5]=unit&populate[6]=user_access_groups.access_group.access_points&populate[7]=verifications&populate[8]=license&populate[9]=image`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return data;
};

const userByIdRequestNoToken = async (userId: number) => {
  const { data }: { data: User } = await instance.get(
    `/api/users/${userId}?populate[0]=access_points&populate[1]=company&populate[2]=created_by_users&populate[3]=property&populate[4]=role&populate[5]=unit&populate[6]=user_access_groups.access_group.access_points&populate[7]=verifications&populate[8]=license&populate[9]=image`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const userByIdRequestTwo = async (userId: number) => {
  const { data }: { data: User } = await instance.get(
    `/api/users/${userId}?populate[0]=access_points&populate[1]=company&populate[2]=created_by_users&populate[3]=property&populate[4]=role&populate[5]=unit&populate[6]=user_access_groups.access_group.access_points&populate[7]=verifications&populate[8]=license`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const usersListRequest = async (
  searchQuery?: string,
  property?: number | null,
  unit?: number | null,
  role?: string,
  roleTwo?: string,
  roleThree?: string,
) => {
  let initialStates = `populate=*&filters[deleted_at][$null]=true`;
  if (searchQuery) {
    initialStates += `&filters[name][$containsi]=${searchQuery}`;
  }
  if (property) {
    initialStates += `&filters[property][id][$eq]=${property}`;
  }
  if (unit) {
    initialStates += `&filters[unit][id][$eq]=${unit}`;
  }
  if (role) {
    initialStates += `&filters[role][name][$startsWith]=${role}`;
  }
  if (roleTwo) {
    initialStates += `&filters[role][name][$startsWith]=${roleTwo}`;
  }
  if (roleThree) {
    initialStates += `&filters[role][name][$startsWith]=${roleThree}`;
  }
  const { data }: { data: User[] } = await instance.get(
    `/api/users?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const getRoles = async () => {
  const { data }: { data: User } = await instance.get(
    `/api/users-permissions/roles`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const usersListPaginationRequest = async (
  searchQuery?: string,
  property?: number | null,
  unit?: number | null,
  role?: string,
  roleTwo?: string,
  roleThree?: string,
  page?: number,
  pageSize?: number,
) => {
  let initialStates = `populate=*&filters[deleted_at][$null]=true&pagination[page]=${page}&pagination[pageSize]=${pageSize}`;
  if (searchQuery) {
    initialStates += `&filters[name][$containsi]=${searchQuery}`;
  }
  if (property) {
    initialStates += `&filters[property][id][$eq]=${property}`;
  }
  if (unit) {
    initialStates += `&filters[unit][id][$eq]=${unit}`;
  }
  if (role) {
    initialStates += `&filters[role][name][$startsWith]=${role}`;
  }
  if (roleTwo) {
    initialStates += `&filters[role][name][$startsWith]=${roleTwo}`;
  }
  if (roleThree) {
    initialStates += `&filters[role][name][$startsWith]=${roleThree}`;
  }
  const { data }: { data: User[] } = await instance.get(
    `/api/users?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const manualRegistrationUsersRequest = async (propertyId: number) => {
  const users: User[] = [];
  while (true) {
    const { data }: { data: User[] } = await instance.get("/api/users", {
      params: {
        "populate[0]": "unit",
        "filters[property][id][$eq]": propertyId,
        "filters[unit][property][id][$eq]": propertyId,
        "filters[deleted_at][$null]": true,
        "sort[0]": "id:asc",
        start: users.length,
        limit: 100,
      },
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (data.length === 0) return users;
    users.push(...data);
  }
};

const usersByUnitListRequest = async (unitId: number, rolesId?: number[]) => {
  let initialStates = `populate=*&filters[unit][id][$eq]=${unitId}`;
  if (rolesId && rolesId.length > 0) {
    rolesId.forEach((id, index) => {
      initialStates += `&filters[role][id][$in][${index}]=${id}`;
    });
  }
  const { data }: { data: User[] } = await instance.get(
    `/api/users?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const getVisitorUsersByUnit = async (unitId: number) => {
  const { data }: { data: User[] } = await instance.get(
    `/api/users?populate=*&filters[unit][id][$eq]=${unitId}&filters[role][id][$eq]=8`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface CreateUserPayload {
  username?: string;
  name?: string;
  email?: string;
  phone?: string;
  unit?: number;
  property?: number | null;
  company?: number;
  password?: string;
  access_points?: number[];
  created_by_users?: number[];
  blocked?: boolean;
  last_access_selected_id?: number;
  user_access_groups?: number[];
  license?: number;
  deleted_at?: string | null;
  confirmed?: false;
}

export interface UserConfirmationEmailPayload {
  username: string;
  role: number;
  email: string;
}

export interface UserCreatedResponse {
  user: User;
  confirmation_token?: String;
}

const createUser = async (params: CreateUserPayload) => {
  const { data }: { data: UserCreatedResponse } = await instance.post(
    `/api/custom-auth/custom-register`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

interface UserPassword {
  code: string;
  password: string;
  passwordConfirmation: string;
}

const setUserPassword = async (params: UserPassword) => {
  const { data }: { data: any } = await instance.post(
    `/auth/reset-password`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const getVisitorUserByPhone = async (userPhone: string, userEmail?: string) => {
  const fixedPhone = `%2B${userPhone.substring(1)}`;
  let initialStates = `?populate=*&filters[$or][0][phone][$eq]=${fixedPhone}&filters[role][id][$eq]=8`;
  if (userEmail) {
    const encodedEmail = encodeURIComponent(userEmail);
    initialStates += `&filters[$or][1][email][$eq]=${encodedEmail}`;
  }
  const { data }: { data: User[] } = await instance.get(
    `/api/users${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const userByEmailRequest = async (email: string) => {
  const encodedEmail = encodeURIComponent(email.trim().toLowerCase());
  const { data }: { data: User[] } = await instance.get(
    `/api/users?populate=*&filters[deleted_at][$null]=true&filters[email][$eqi]=${encodedEmail}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const updateUser = async (userId: number, params: CreateUserPayload) => {
  const { data }: { data: User } = await instance.put(
    `/api/users/${userId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const addUserRole = async (userId: number, role: number) => {
  const { data }: { data: User } = await instance.put(
    `/api/users/${userId}`,
    {
      role,
    },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const addUserCreatedBy = async (userId: number, created_by: number[]) => {
  const { data }: { data: User } = await instance.put(
    `/api/users/${userId}`,
    {
      created_by_users: created_by,
    },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const sendConfirmationEmail = async (params: {
  email: string;
  confirmation_token: string;
}) => {
  const { data }: { data: User } = await instance.post(
    `/api/custom-auth/send-email`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const resendConfirmationEmail = async (params: { email: string }) => {
  const { data } = await instance.post(
    `/api/custom-auth/resend-confirmation`,
    { email: params.email },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const deleteUserRequest = async (userId: number) => {
  const { data }: { data: User } = await instance.delete(
    `/api/users/${userId}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//ACCESS KEYS------------------------------------------------->>

const accessKeysRequest = async (page: number, pageSize: number) => {
  const { data }: { data: AccessKeyArray } = await instance.get(
    `/api/access-keys?populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const accessKeysByPropertyRequest = async (
  propertyId: number,
  page: number,
  pageSize: number,
  type?: "visitor" | "frequent" | "provider",
  lastTransactionType?: "access" | "exit" | "try" | "deleted" | "unused",
  access_point?: number | null,
  from_date?: string,
  to_date?: string,
  date?: string,
  visitor?: string,
  unitId?: number,
  createdByUserId?: number,
) => {
  let initialStates = `populate[0]=created.unit&populate[1]=property&populate[2]=verified_visitor&populate[3]=one_time_visitor.provider&populate[4]=access_groups&populate[5]=verifiable_ids&populate[6]=requirements.requirement_type&populate[7]=unit&filters[property][id][$eq]=${propertyId}&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort=createdAt:desc`;
  if (type !== undefined) {
    initialStates += `&filters[access_key_type][$eq]=${type}`;
  }
  if (lastTransactionType !== undefined) {
    if (lastTransactionType === "try") {
      initialStates += `&filters[last_transaction_type][$containsi]=try`;
    }
    if (lastTransactionType === "access") {
      // initialStates += `&filters[last_transaction_type][$eq]=access`;
      initialStates += `&filters[last_transaction_type][$containsi]=entrance`;
    }
    if (lastTransactionType === "exit") {
      initialStates += `&filters[last_transaction_type][$containsi]=exit`;
    }
    if (lastTransactionType === "deleted") {
      initialStates += `&filters[daleted_at][$notNull]=true`;
    }
    if (lastTransactionType === "unused") {
      initialStates += `&filters[last_transaction_type][$null]=true&filters[daleted_at][$null]=true`;
    }
  }
  if (access_point !== undefined) {
    initialStates += `&filters[access_points][id][$eq]=${access_point}`;
  }
  if (from_date !== undefined && to_date !== undefined) {
    initialStates += `&filters[valid_from][$between][]=${from_date}&filters[valid_from][$between][]=${to_date}`;
  }
  if (date !== undefined) {
    initialStates += `&filters[valid_from][$eq]=${date}`;
  }
  if (visitor) {
    initialStates += `&filters[$or][0][verified_visitor][name][$containsi]=${visitor}`;
    initialStates += `&filters[$or][1][one_time_visitor][name][$containsi]=${visitor}`;
    initialStates += `&filters[$or][2][one_time_visitor][provider][name][$containsi]=${visitor}`;
    initialStates += `&filters[$or][3][one_time_visitor][provider_name][$containsi]=${visitor}`;
    initialStates += `&filters[$or][4][identifier][$containsi]=${encodeURIComponent(
      visitor,
    )}`;
  }
  if (unitId !== undefined) {
    initialStates += `&filters[unit][id][$eq]=${unitId}`;
  }
  if (createdByUserId !== undefined) {
    initialStates += `&filters[created][id][$eq]=${createdByUserId}`;
  }
  const { data }: { data: AccessKeyAllResponse } = await instance.get(
    `/api/access-keys/all?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const accessKeysByIdRequest = async (documentId: string) => {
  const { data }: { data: SingleVerifiableId } = await instance.get(
    `/api/verifiable-ids/${documentId}?populate[0]=access_key_qr.created.unit&populate[1]=access_key_qr.property&populate[2]=access_key_qr.verified_visitor&populate[3]=access_key_qr.one_time_visitor.provider&populate[4]=access_key_qr.access_groups.access_points&populate[5]=access_key_qr.requirements.requirement_type&populate[6]=access_key_qr.access_points`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const singleAccessKeyByIdRequest = async (accessKeyId: number) => {
  const { data }: { data: AccessKeyCreateResponse } = await instance.get(
    `/api/access-keys/${accessKeyId}?populate[0]=created.unit&populate[1]=property&populate[2]=verified_visitor&populate[3]=one_time_visitor.provider`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const accessKeysByUserIdRequest = async (userId: number) => {
  const { data }: { data: AccessKeyCreateResponse[] } = await instance.get(
    `/api/access-keys/?populate[0]=created.unit&populate[1]=property&populate[2]=verified_visitor&populate[3]=one_time_visitor.provider&populate[4]=verifiable_ids&populate[5]=access_groups.access_points&filters[verified_visitor][id][$eq]=${userId}&filters[access_key_type][$eq]=user&filters[daleted_at][$null]=true&sort=createdAt:desc`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const invitationByUuidRequest = async (uuid: string) => {
  const { data }: { data: VerifiableIdArray2 } = await instance.get(
    `/api/verifiable-ids?filters[uuid][$eq]=${encodeURIComponent(uuid)}&populate[0]=access_key_qr.created.unit&populate[1]=access_key_qr.property&populate[2]=access_key_qr.verified_visitor&populate[3]=access_key_qr.one_time_visitor.provider&populate[4]=access_key_qr.access_groups.access_points&populate[5]=access_key_qr.requirements.requirement_type&populate[6]=access_key_qr.access_points&populate[7]=access_key_qr.unit`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const invitationAccessKeyByUuidRequest = async (uuid: string) => {
  const { data }: { data: AccessKeyCreateResponse[] | { data: AccessKeyCreateResponse[] } } =
    await instance.get(
      `/api/access-keys?filters[uuid][$eq]=${encodeURIComponent(uuid)}&populate[0]=created.unit&populate[1]=property&populate[2]=verified_visitor&populate[3]=one_time_visitor.provider&populate[4]=access_groups.access_points&populate[5]=verifiable_ids&populate[6]=access_points&populate[7]=requirements.requirement_type&populate[8]=unit`,
      {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      },
    );

  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === "object" && Array.isArray((data as { data?: AccessKeyCreateResponse[] }).data)) {
    return (data as { data: AccessKeyCreateResponse[] }).data;
  }

  return [];
};

const getInvitationByIdentifier = async (
  identifier: string,
  propertyId: number,
) => {
  const { data }: { data: AccessKeyCreateResponse[] } = await instance.get(
    `/api/access-keys?filters[identifier][$eq]=${identifier}&filters[property][id][$eq]=${propertyId}&filters[daleted_at][$null]=true&populate[0]=verifiable_ids`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const getInvitationByIdentifier2 = async (
  identifier: string,
  propertyId: number,
) => {
  const { data }: { data: AccessKeyCreateResponse[] } = await instance.get(
    `/api/access-keys?filters[identifier][$eq]=${identifier}&filters[property][id][$eq]=${propertyId}&populate[0]=verifiable_ids&sort=createdAt:desc`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

export interface CreateAccessKeyRequest {
  created?: number;
  verified_visitor?: number;
  one_time_visitor?: number;
  valid_from?: string;
  valid_until?: string;
  access_key_type?: string;
  reason_for_visit?: string;
  property?: number;
  access_points?: number[];
  provider?: number;
  other_provider_name?: string;
  unit?: number;
  access_groups?: number[];
  antipassback?: number;
  access_counter?: number;
  identifier?: string;
  daleted_at?: string;
  pass_type?: "entrance" | "exit";
  authorization_passes?: number[];
  dynamic_duration?: number;
  exit_restriction?: string | null;
  configuration?: {
    week_days?: string[];
  };
}

const createAccessKeyRequest = async (params: CreateAccessKeyRequest) => {
  const { data }: { data: AccessKeyCreateResponse } = await instance.post(
    `/api/access-keys`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const createAccessKeyRequestNoToken = async (
  params: CreateAccessKeyRequest,
) => {
  const { data }: { data: AccessKeyCreateResponse } = await instance.post(
    `/api/access-keys`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

export interface SendAuthorizationRequest {
  pass_id: string;
  receiver_docid?: string;
}

export interface SendAuthorizationResponse {
  success: boolean;
  results: {
    whatsapp: [
      {
        userId: number;
        status: string;
      },
    ];
    email: [
      {
        userId: number;
        status: string;
      },
    ];
    url: string;
  };
}

const sendAuthorizationRequest = async (params: SendAuthorizationRequest) => {
  const { data }: { data: SendAuthorizationResponse } = await instance.post(
    `/api/approval-notification`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const updateAccessKeyRequest = async (
  accessKeyId: number,
  params: CreateAccessKeyRequest,
) => {
  const { data }: { data: AccessKeyCreateResponse } = await instance.put(
    `/api/access-keys/${accessKeyId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const verifiableIdByUuidRequest = async (uuid: string) => {
  const { data }: { data: VerifiableIdArray2 } = await instance.get(
    `/api/verifiable-ids?filters[uuid]=${uuid}&populate[0]=access_key_qr.created&populate[1]=access_key_qr.property&populate[2]=access_key_qr.verified_visitor&populate[3]=access_key_qr.one_time_visitor.provider&populate[4]=access_key_qr.access_groups.access_points&populate[5]=access_key_qr.access_groups.requirement_types&populate[6]=access_key_qr.unit&populate[7]=access_key_qr.requirements.requirement_type&populate[8]=access_key_qr.access_points`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//ACCESS POINTS----------------------------------------------->>

const accessPointsRequest = async (
  propertyId: number,
  page: number,
  pageSize: number,
  name?: string,
) => {
  let initialStates = `populate=*&filters[property][id][$eq]=${propertyId}&pagination[page]=${page}&pagination[pageSize]=${pageSize}`;
  if (name) {
    initialStates += `&filters[name][$containsi]=${name}`;
  }
  const { data }: { data: AccessPointArray } = await instance.get(
    `/api/access-points?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const accessPointsCISORequest = async (
  companyId: number,
  propertyId: number,
  page: number,
  pageSize: number,
  name?: string,
) => {
  let initialStates = `populate[0]=property.company&populate[1]=gates&populate[2]=photo_types&filters[property][company][id][$eq]=${companyId}&filters[property][id][$eq]=${propertyId}&pagination[page]=${page}&pagination[pageSize]=${pageSize}`;
  if (name) {
    initialStates += `&filters[name][$containsi]=${name}`;
  }
  const { data }: { data: AccessPointArray } = await instance.get(
    `/api/access-points?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const accessPointByIdRequest = async (documentId: string) => {
  const { data }: { data: AccessPoint } = await instance.get(
    `/api/access-points/${documentId}?populate=*`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const accessPointByIdRequestNoToken = async (documentId: string) => {
  const { data }: { data: AccessPointRelation } = await instance.get(
    `/api/access-points/${documentId}?populate=*`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const accessPointByIdRequestToken = async (
  documentId: string,
  token: string,
) => {
  const { data }: { data: AccessPointRelation } = await instance.get(
    `/api/access-points/${documentId}?populate=*`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return data;
};

const accessPointsByCompanyRequest = async (companyId: number) => {
  let initialStates = `populate[0]=property.company&populate[1]=gates&populate[2]=photo_types&filters[property][company][id][$eq]=${companyId}`;
  const { data }: { data: AccessPointArray } = await instance.get(
    `/api/access-points?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface AccessPointPayload {
  data: {
    name?: string;
    photo_types?: number[];
  };
}

const updateAccessPoint = async (
  documentId: string,
  params: AccessPointPayload,
) => {
  const { data }: { data: AccessPointRelation } = await instance.put(
    `/api/access-points/${documentId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//UNITS------------------------------------------------------->>

const unitsRequest = async (
  property: number,
  page: number,
  pageSize: number,
  name?: string,
) => {
  let initialStates = `?populate=*&filters[property][id][$eq]=${property}&pagination[page]=${page}&pagination[pageSize]=${pageSize}`;
  if (name) {
    initialStates += `&filters[name][$containsi]=${name}`;
  }
  const { data }: { data: UnitArray } = await instance.get(
    `/api/units${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const unitByIdRequest = async (property: number, documentId: string) => {
  const { data }: { data: Unit } = await instance.get(
    `/api/units/${documentId}?populate=*&filters[property][id][$eq]=${property}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface CreateUnitRequest {
  data: {
    name?: string;
    property?: number | null;
    access_points?: number[];
  };
}

const createUnitRequest = async (params: CreateUnitRequest) => {
  const { data }: { data: StrapiUnitRelation } = await instance.post(
    `/api/units`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const editUnitRequest = async (
  documentId: string,
  params: CreateUnitRequest,
) => {
  const { data }: { data: StrapiUnitRelation } = await instance.put(
    `/api/units/${documentId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//PHOTO TYPES------------------------------------------------->>

const photoTypesRequest = async () => {
  const { data }: { data: PhotoTypesArray } = await instance.get(
    `/api/photo-types?populate=*`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//PROPERTIES-------------------------------------------------->>

const propertiesByCompanyRequest = async (companyId: number, name?: string) => {
  let initialStates = `populate[0]=users.role&populate[1]=company&populate[2]=license&populate[3]=units&populate[4]=access_points&filters[company][id][$eq]=${companyId}`;
  if (name) {
    initialStates += `&filters[name][$containsi]=${name}`;
  }
  const { data }: { data: PropertyArray } = await instance.get(
    `/api/properties?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//ONE TIME VISITORS------------------------------------------->>

const getVisitorsByUser = async (userId: string, phone?: string) => {
  let initialStates = `populate[0]=created_by_user&populate[1]=provider&filters[created_by_user][id][$eq]=${userId}`;
  if (phone) {
    const fixedPhone = `%2B${phone.substring(1)}`;
    initialStates += `&filters[phone][$eq]=${fixedPhone}`;
  }
  const { data }: { data: OneTimeVisitorArray } = await instance.get(
    `/api/one-time-visitors?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const getVisitorsByUserNoToken = async (userId: string, phone?: string) => {
  let initialStates = `populate[0]=created_by_user&populate[1]=provider&filters[created_by_user][id][$eq]=${userId}`;
  if (phone) {
    const fixedPhone = `%2B${phone.substring(1)}`;
    initialStates += `&filters[phone][$eq]=${fixedPhone}`;
  }
  const { data }: { data: OneTimeVisitorArray } = await instance.get(
    `/api/one-time-visitors?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const getUserContacts = async (
  userId: string,
  page: number,
  pageSize: number,
  visitor_name?: string,
) => {
  let initialStates = `populate[0]=created_by_user&populate[1]=provider&filters[created_by_user][id][$eq]=${userId}&filters[provider][$null]=true&filters[provider_name][$null]=true&pagination[page]=${page}&pagination[pageSize]=${pageSize}`;
  if (visitor_name) {
    initialStates += `&filters[$or][0][name][$containsi]=${visitor_name}`;
  }
  const { data }: { data: OneTimeVisitorArray } = await instance.get(
    `/api/one-time-visitors?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface OneTimeVisitorPayload {
  data: {
    name?: string;
    email?: string;
    phone?: string;
    created_by_user?: number;
    provider?: number;
    provider_name?: string;
  };
}

const createOneTimeVisitor = async (params: OneTimeVisitorPayload) => {
  const { data }: { data: OneTimeVisitor } = await instance.post(
    `/api/one-time-visitors`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data.data;
};

const createOneTimeVisitorNoToken = async (params: OneTimeVisitorPayload) => {
  const { data }: { data: OneTimeVisitor } = await instance.post(
    `/api/one-time-visitors`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data.data;
};

const updateOneTimeVisitor = async (
  documentId: string,
  params: OneTimeVisitorPayload,
) => {
  const { data }: { data: OneTimeVisitor } = await instance.put(
    `/api/one-time-visitors/${documentId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data.data;
};

//------------------------------------------------------------>>
//ROLES------------------------------------------------------->>

const userRolesRequest = async () => {
  const { data }: { data: UserRoles } = await instance.get(
    `/api/users-permissions/roles`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//UPLOAD------------------------------------------------------>>

export interface UploadMediaRequest {
  files: File[];
  ref: string;
  refId: number;
  field: string;
}

const uploadMediaRequest = async (params: UploadMediaRequest) => {
  const formData = new FormData();

  if (params.files) {
    params.files.forEach((file) => {
      formData.append("files", file);
    });
  }
  formData.append("ref", params.ref);
  formData.append("refId", params.refId.toString());
  formData.append("field", params.field);

  const { data }: { data: MediaResponse } = await instance.post(
    `/api/upload`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
        // Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return data;
};

const deleteMediaRequest = async (mediaId: number) => {
  const { data }: { data: MediaResponse } = await instance.delete(
    `/api/upload/files/${mediaId}`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return data;
};

// const uploadMediaRequest = async (params: UploadMediaRequest) => {
//   const formData = new FormData();

//   if (params.files) {
//     params.files.forEach((file) => {
//       formData.append("files", file);
//     });
//   }
//   formData.append("ref", params.ref);
//   formData.append("refId", params.refId.toString());
//   formData.append("field", params.field);

//   const { data }: { data: MediaResponse } = await instance.post(
//     `/api/upload`,
//     formData,
//     {
//       headers: {
//         Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
//         "Content-Type": "multipart/form-data",
//       },
//     }
//   );

//   return data;
// };

//------------------------------------------------------------>>
//ACCESS GROUPS----------------------------------------------->>

const accessGroupsRequest = async (
  propertyId: number,
  page: number,
  pageSize: number,
  unitAccessGroup: boolean,
  name?: string,
) => {
  let initialStates = `populate[0]=access_key_qrs&populate[1]=access_points&populate[2]=banned_users&populate[3]=company&populate[4]=properties&populate[5]=role_permissions_users&populate[6]=user_access_groups.user&populate[7]=requirement_types&filters[properties][id][$eq]=${propertyId}&pagination[page]=${page}&pagination[pageSize]=${pageSize}`;
  if (name) {
    initialStates += `&filters[name][$containsi]=${encodeURIComponent(name)}`;
  }
  if (unitAccessGroup === true) {
    initialStates += `&filters[unit_access_group][$eq]=true`;
  } else {
    initialStates += `&filters[$or][0][unit_access_group][$eq]=false`;
    initialStates += `&filters[$or][1][unit_access_group][$null]=true`;
  }
  const { data }: { data: AccessGroupsArray } = await instance.get(
    `/api/access-groups?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const visitorAccessGroupsRequest = async (propertyId: number) => {
  let initialStates = `populate=*&filters[properties][id][$eq]=${propertyId}&filters[role_permissions_users][name][$startsWith]=visitor`;
  const { data }: { data: AccessGroupsArray } = await instance.get(
    `/api/access-groups?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const visitorAccessGroupsForPassRequest = async (
  propertyId: number,
  accessPointId: number,
) => {
  const groups: AccessGroupsArray["data"] = [];
  let page = 1;
  let pageCount = 1;
  do {
    const { data }: { data: AccessGroupsArray } = await instance.get(
      `/api/access-groups`,
      {
        params: {
          "filters[properties][id][$eq]": propertyId,
          "filters[role_permissions_users][name][$startsWith]": "visitor",
          "filters[access_points][id][$eq]": accessPointId,
          "pagination[page]": page,
          "pagination[pageSize]": 100,
          "sort[0]": "id:asc",
        },
        headers: { Authorization: `Bearer ${apiToken}` },
      },
    );
    groups.push(...data.data);
    pageCount = data.meta.pagination.pageCount;
    page += 1;
  } while (page <= pageCount);
  return [...new Set(groups.map((group) => group.id))];
};

const userAccessGroupsRequest = async (
  propertyId: number,
  roleName: string,
) => {
  let initialStates = `populate[0]=access_points&populate[1]=role_permissions_users&populate[2]=requirement_types&filters[properties][id][$eq]=${propertyId}&filters[role_permissions_users][name][$startsWith]=${roleName}&filters[$or][0][unit_access_group][$eq]=false&filters[$or][1][unit_access_group][$null]=true`;
  const { data }: { data: AccessGroupsArray } = await instance.get(
    `/api/access-groups?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const accessGroupsByAccessPointRequest = async (accessPointId: number) => {
  let initialStates = `populate[0]=access_points&populate[1]=role_permissions_users&populate[2]=requirement_types&populate[3]=banned_users&filters[access_points][id][$eq]=${accessPointId}`;
  const { data }: { data: AccessGroupsArray } = await instance.get(
    `/api/access-groups?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const accessGroupsByIdRequest = async (documentId: string) => {
  let initialStates = `populate=*`;
  const { data }: { data: AccessGroups } = await instance.get(
    `/api/access-groups/${documentId}?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface CreateAccGroupRequest {
  data: {
    name?: string;
    from?: string;
    to?: string;
    // anti_passback?: boolean;
    company?: number | null;
    access_points?: number[];
    role_permissions_users?: number[];
    configuration?: CreateConfigAccGroup[];
    banned_users?: number[];
    properties?: number[];
    unit_access_group?: boolean;
  };
}

export interface CreateConfigAccGroup {
  id?: number;
  week_days?: string[];
  start_time?: string;
  end_time?: string;
  exit_restriction_from?: string | null;
  exit_restriction_to?: string | null;
  antipassback?: number;
  description?: string;
  name?: string;
  register?: boolean;
  photos?: string[];
  printable_pass?: boolean;
}

const createAccessGroupRequest = async (params: CreateAccGroupRequest) => {
  const { data }: { data: AccessGroups } = await instance.post(
    `/api/access-groups`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface RegenerateVerifiableIdUUIDPayload {
  data: {
    id: string;
  };
}

const regenerateVerifiableId = async (
  accessKeyId: number,
  params: RegenerateVerifiableIdUUIDPayload,
) => {
  const { data }: { data: RegenerateVerifiableIdResponse } =
    await instance.post(
      `/api/access-keys/${accessKeyId}/regenerate-id`,
      params,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );

  return data;
};

const updateAccessGroupRequest = async (
  documentId: string,
  params: CreateAccGroupRequest,
) => {
  const { data }: { data: AccessGroupsRelation } = await instance.put(
    `/api/access-groups/${documentId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const deleteAccessGroupRequest = async (documentId: string) => {
  const { data }: { data: AccessGroupsRelation } = await instance.delete(
    `/api/access-groups/${documentId}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface CreateRequirementTypeRequest {
  data: {
    name: string;
    description: string;
    data_type: "string" | "boolean" | "selector";
    required: boolean;
    access_key: boolean;
    transaction: boolean;
    selector_data?: string[];
    access_groups: number[];
    url?: string;
  };
}

const createRequirementTypeRequest = async (
  params: CreateRequirementTypeRequest,
) => {
  const { data }: { data: { id: number } } = await instance.post(
    `/api/requirement-types`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const updateRequirementTypeRequest = async (
  documentId: string,
  params: CreateRequirementTypeRequest,
) => {
  const { data }: { data: { id: number } } = await instance.put(
    `/api/requirement-types/${documentId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const deleteRequirementTypeRequest = async (documentId: string) => {
  const { data } = await instance.delete(
    `/api/requirement-types/${documentId}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//PROVIDERS--------------------------------------------------->>

const providersRequest = async () => {
  const { data }: { data: ProviderArray } = await instance.get(
    `/api/providers`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//USER-ACCESS-GROUP------------------------------------------->>

const getUserAccessGroupByUserRequest = async (userId: number) => {
  const { data }: { data: UserAccessGroupsArray } = await instance.get(
    `/api/user-access-groups?filters[user][id][$eq]=${userId}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface CreateUserAccGroupRequest {
  data: {
    user?: number;
    access_group?: number;
    access?: boolean;
  };
}

const createUserAccessGroup = async (params: CreateUserAccGroupRequest) => {
  const { data }: { data: UserAccessGroups } = await instance.post(
    `/api/user-access-groups`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const deleteUserAccessGroup = async (documentId: string) => {
  const { data }: { data: UserAccessGroups } = await instance.delete(
    `/api/user-access-groups/${documentId}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//ACCESS-TRANSACTION------------------------------------------>>

export interface CreateTransactionPayload {
  data: {
    datetime?: string;
    access_type?: string; //access or exit
    access_key_type?: string; // 1: visit, 2: temporary, 3: event, 4: provider, 5: dinamic-key
    access_key_id?: string;
    gate?: number; //relación con gate
    // access_point?: number; //relación con access point
    access_transaction_photos?: number[];
    users_permissions_users?: number[];
    status?: {
      message: {
        en: string;
        es: string;
      };
      type: "entrance" | "exit" | "try";
    } | null;
  };
}

const createTransaction = async (params: CreateTransactionPayload) => {
  const { data }: { data: Transaction } = await instance.post(
    `/api/access-transactions`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data.data;
};

const createTransactionNoToken = async (params: CreateTransactionPayload) => {
  const { data }: { data: Transaction } = await instance.post(
    `/api/access-transactions`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data.data;
};

//------------------------------------------------------------>>
//ACCESS------------------------------------------------------>>

const getAccessesRequest = async (
  propertyId: number,
  sorting: "createdAt" | "updatedAt",
  page: number,
  pageSize: number,
  accessPointId?: number,
  visitor?: string,
  unit?: string,
  dateFromISO?: string,
  dateToISO?: string,
  state?: "" | "entry" | "exit",
  visitedUserName?: string,
) => {
  let initialStates = `populate[0]=entrance_transaction.access_transaction_photos.image&populate[1]=exit_transaction&populate[2]=property&populate[3]=entrance_transaction.gate.access_point&populate[4]=exit_transaction.gate.access_point&populate[5]=entrance_transaction.users_permissions_users&populate[6]=exit_transaction.users_permissions_users&filters[property][id][$eq]=${propertyId}&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort=${sorting}:desc`;
  if (visitor) {
    initialStates += `&filters[visitor][$containsi]=${encodeURIComponent(
      visitor,
    )}`;
  }
  if (unit) {
    initialStates += `&filters[unit][$eq]=${encodeURIComponent(unit)}`;
  }
  if (visitedUserName) {
    initialStates += `&filters[user][$eqi]=${encodeURIComponent(visitedUserName)}`;
  }
  if (dateFromISO && dateToISO) {
    const from = encodeURIComponent(dateFromISO);
    const to = encodeURIComponent(dateToISO);

    initialStates += `&filters[$and][0][$or][0][entrance_transaction][datetime][$between][0]=${from}`;
    initialStates += `&filters[$and][0][$or][0][entrance_transaction][datetime][$between][1]=${to}`;

    initialStates += `&filters[$and][0][$or][1][exit_transaction][datetime][$between][0]=${from}`;
    initialStates += `&filters[$and][0][$or][1][exit_transaction][datetime][$between][1]=${to}`;
  }
  if (accessPointId) {
    initialStates += `&filters[$and][1][$or][0][entrance_transaction][gate][access_point][id][$eq]=${accessPointId}`;
    initialStates += `&filters[$and][1][$or][1][exit_transaction][gate][access_point][id][$eq]=${accessPointId}`;
  }
  if (state === "entry") {
    // solo registros con entrada
    initialStates += `&filters[entrance_transaction][id][$notNull]=true`;
  } else if (state === "exit") {
    // solo registros con salida
    initialStates += `&filters[exit_transaction][id][$notNull]=true`;
  }
  const { data }: { data: AccessArray } = await instance.get(
    `/api/accesses?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const getAccessesByIdRequest = async (documentId: string) => {
  let initialStates = `populate[0]=entrance_transaction.access_transaction_photos.image&populate[1]=exit_transaction&populate[2]=property&populate[3]=entrance_transaction.gate.access_point&populate[4]=exit_transaction.gate.access_point&populate[5]=entrance_transaction.requirements.requirement_type&populate[6]=entrance_transaction.users_permissions_users&populate[7]=exit_transaction.users_permissions_users`;
  const { data }: { data: Access } = await instance.get(
    `/api/accesses/${documentId}?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data.data;
};

const getNoExitAccessesRequest = async (
  propertyId: number,
  visitorName?: string,
  // accessPointId?: number
) => {
  let initialStates = `populate[0]=entrance_transaction.access_transaction_photos.image&populate[1]=property&populate[2]=entrance_transaction.gate.access_point&filters[property][id][$eq]=${propertyId}&filters[exit_transaction][$null]=true&sort=createdAt:desc`;
  // if (accessPointId) {
  //   initialStates += `&filters[entrance_transaction][gate][access_point][id][$eq]=${accessPointId}`;
  // }
  if (visitorName) {
    initialStates += `&filters[visitor][$containsi]=${visitorName}`;
  }
  const { data }: { data: AccessArray } = await instance.get(
    `/api/accesses?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const getAccessesByAccessKeyRequest = async (
  propertyId: number,
  accessKeyId: number,
) => {
  let initialStates = `populate[0]=entrance_transaction.access_transaction_photos.image&populate[1]=property&populate[2]=entrance_transaction.gate.access_point&filters[property][id][$eq]=${propertyId}&filters[exit_transaction][$null]=true&sort=createdAt:asc`;
  if (accessKeyId) {
    initialStates += `&filters[access_key_id][id][$eq]=${accessKeyId}`;
  }
  const { data }: { data: AccessArray } = await instance.get(
    `/api/accesses?${initialStates}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface CreateAccessPayload {
  data: {
    property?: number;
    company?: number;
    access_key_id?: number;
    entrance_transaction?: number; //relación transaction
    exit_transaction?: number; //relación transaction
    user?: string;
    visitor?: string;
    unit?: string;
    authorized?: boolean;
  };
}

const createAccess = async (params: CreateAccessPayload) => {
  const { data }: { data: AccessRelation } = await instance.post(
    `/api/accesses`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const createAccessNoToken = async (params: CreateAccessPayload) => {
  const { data }: { data: AccessRelation } = await instance.post(
    `/api/accesses`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const updateAccess = async (
  documentId: string,
  params: CreateAccessPayload,
) => {
  const { data }: { data: AccessRelation } = await instance.put(
    `/api/accesses/${documentId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//ACCESS------------------------------------------------------>>

export interface CreateAccessTransactionPhotoPayload {
  data: {
    url?: string;
    photo_type?: "photo id" | "plates" | "equipment" | "visitor" | null;
    access_transaction?: number;
    type?: string;
  };
}

const createAccessTransactionPhoto = async (
  params: CreateAccessTransactionPhotoPayload,
) => {
  const { data }: { data: TransactionPhoto } = await instance.post(
    `/api/access-transaction-photos`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>
//MEDIA------------------------------------------------------->>

const mediaContent = async (formData: FormData) => {
  const { data }: { data: MediaImageSingle } = await instance.post(
    `/api/upload`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    },
  );

  return data;
};

//------------------------------------------------------------>>

export interface CreateVerifiableIdPayload {
  data: {
    uuid?: string;
    valid_until?: number;
    type: "qrcode" | "barcode" | "biometrics" | "card" | "camera";
    allows_unauthenticated_regeneration?: boolean;
    access_key_qr?: number;
  };
}

const createVerifiableId = async (params: CreateVerifiableIdPayload) => {
  const { data }: { data: StrapiVerifiableIdRelation } = await instance.post(
    `/api/verifiable-ids`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const createVerifiableIdNoToken = async (params: CreateVerifiableIdPayload) => {
  const { data }: { data: StrapiVerifiableIdRelation } = await instance.post(
    `/api/verifiable-ids`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const validateKey = async (params: { gate_id: number; key: string }) => {
  const { data }: { data: MessageResponse } = await instance.post(
    `/api/access-keys/validate-key`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

export interface CreateRequirementPayload {
  data: {
    requirement_type?: number;
    access_key_qr?: number;
    access_transaction?: { connect: string[] } | undefined;
    url?: string;
    value?: string;
  };
}

const createRequirement = async (params: CreateRequirementPayload) => {
  const { data }: { data: MessageResponse } = await instance.post(
    `/api/requirements`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const updateRequirement = async (
  documentId: string,
  params: CreateRequirementPayload,
) => {
  const { data }: { data: MessageResponse } = await instance.put(
    `/api/requirements/${documentId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data;
};

const authorizationPassByIdRequest = async (documentId: string) => {
  const { data }: { data: AuthorizationPass } = await instance.get(
    `/api/authorization-passes/${documentId}?populate[0]=access_key_qr&populate[1]=access_point.gates&populate[2]=unit.property.company&populate[3]=user&populate[4]=confirmed_by&populate[5]=access_transaction`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data.data;
};

export interface CreateAuthorizationPassPayload {
  data: {
    user?: number;
    visitor_name?: string;
    visitor_phone?: string;
    unit?: number;
    access_point?: number;
    type?: "entrance" | "exit" | "visitor";
    confirmed_by?: number;
    access_key_qr?: number;
    access_transaction?: number;
  };
}

const createAuthorizationPass = async (
  params: CreateAuthorizationPassPayload,
) => {
  const { data }: { data: AuthorizationPass } = await instance.post(
    `/api/authorization-passes`,
    params,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  return data.data;
};

const updateAuthorizationPass = async (
  documentId: string,
  params: CreateAuthorizationPassPayload,
) => {
  const { data }: { data: AuthorizationPassRelation } = await instance.put(
    `/api/authorization-passes/${documentId}`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const api = {
  getRoles,
  authorizationPassByIdRequest,
  updateAuthorizationPass,
  createAuthorizationPass,
  loginRequest,
  userByIdRequest,
  accessKeysRequest,
  accessKeysByIdRequest,
  usersListRequest,
  accessPointsRequest,
  unitsRequest,
  createUser,
  sendConfirmationEmail,
  resendConfirmationEmail,
  addUserRole,
  accessPointByIdRequest,
  photoTypesRequest,
  propertiesByCompanyRequest,
  accessPointsCISORequest,
  uploadMediaRequest,
  updateAccessPoint,
  createUnitRequest,
  editUnitRequest,
  unitByIdRequest,
  updateUser,
  accessGroupsRequest,
  accessPointsByCompanyRequest,
  createAccessGroupRequest,
  userRolesRequest,
  updateAccessGroupRequest,
  deleteAccessGroupRequest,
  createRequirementTypeRequest,
  updateRequirementTypeRequest,
  deleteRequirementTypeRequest,
  visitorAccessGroupsRequest,
  visitorAccessGroupsForPassRequest,
  createAccessKeyRequest,
  getVisitorUserByPhone,
  userByEmailRequest,
  accessKeysByPropertyRequest,
  invitationByUuidRequest,
  accessGroupsByAccessPointRequest,
  usersByUnitListRequest,
  manualRegistrationUsersRequest,
  providersRequest,
  addUserCreatedBy,
  getVisitorsByUser,
  createOneTimeVisitor,
  accessKeysByUserIdRequest,
  getUserContacts,
  getVisitorUsersByUnit,
  createUserAccessGroup,
  deleteUserAccessGroup,
  deleteUserRequest,
  accessGroupsByIdRequest,
  setUserPassword,
  usersListPaginationRequest,
  createTransaction,
  createAccess,
  mediaContent,
  createAccessTransactionPhoto,
  getAccessesRequest,
  getNoExitAccessesRequest,
  updateAccess,
  getAccessesByIdRequest,
  createVerifiableId,
  userByIdRequestTwo,
  updateOneTimeVisitor,
  invitationAccessKeyByUuidRequest,
  validateKey,
  verifiableIdByUuidRequest,
  getAccessesByAccessKeyRequest,
  deleteMediaRequest,
  regenerateVerifiableId,
  updateAccessKeyRequest,
  getInvitationByIdentifier,
  singleAccessKeyByIdRequest,
  createRequirement,
  getUserAccessGroupByUserRequest,
  userByIdRequestNoToken,
  accessPointByIdRequestNoToken,
  accessPointByIdRequestToken,
  sendAuthorizationRequest,
  createOneTimeVisitorNoToken,
  getVisitorsByUserNoToken,
  createAccessKeyRequestNoToken,
  createVerifiableIdNoToken,
  userAccessGroupsRequest,
  createTransactionNoToken,
  createAccessNoToken,
  getInvitationByIdentifier2,
  updateRequirement,
};

export default api;
